import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Flame, Check } from 'lucide-react';
import { toast } from 'sonner';
import { DinoLogo } from '@/components/DinoLogo';

const GOAL_OPTIONS = [
  { days: 2, label: 'Casual', description: 'Perfect for busy schedules' },
  { days: 3, label: 'Light', description: 'A few times a week' },
  { days: 4, label: 'Regular', description: 'Most weekdays' },
  { days: 5, label: 'Committed', description: 'Nearly every day' },
  { days: 6, label: 'Dedicated', description: 'Almost daily reading' },
  { days: 7, label: 'Daily', description: 'Every single day' },
];

export default function OnboardingReadingGoal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedGoal, setSelectedGoal] = useState(7); // Default to daily
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      // Save reading goal preference
      const { error } = await supabase
        .from('user_reading_preferences')
        .upsert(
          {
            user_id: user.id,
            weekly_reading_goal: selectedGoal,
          },
          { onConflict: 'user_id' }
        );

      if (error) throw error;

      toast.success('Reading goal set! 🎯');
      navigate('/');
    } catch (error) {
      console.error('Error saving reading goal:', error);
      toast.error('Failed to save reading goal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex items-center justify-center p-4 w-full max-w-full overflow-x-hidden">
      <div className="w-full max-w-4xl space-y-8">
        <div className="text-center space-y-4">
          <DinoLogo className="w-16 h-16 mx-auto" />
          <div>
            <h1 className="text-3xl font-bold mb-2">Set Your Reading Goal 🔥</h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              To stay accountable, commit to a reading streak. Choose how many days per week you want to read.
              Your streak continues as long as you hit your weekly goal!
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {GOAL_OPTIONS.map((option) => (
            <Card
              key={option.days}
              onClick={() => setSelectedGoal(option.days)}
              className={`
                p-6 cursor-pointer transition-all hover:shadow-lg
                ${selectedGoal === option.days 
                  ? 'ring-2 ring-primary bg-primary/5' 
                  : 'hover:bg-muted/50'
                }
              `}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Flame className={`w-5 h-5 ${selectedGoal === option.days ? 'text-orange-500' : 'text-muted-foreground'}`} />
                  <span className="text-2xl font-bold">{option.days}</span>
                  <span className="text-sm text-muted-foreground">days</span>
                </div>
                {selectedGoal === option.days && (
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
              </div>
              <div>
                <p className="font-semibold mb-1">{option.label}</p>
                <p className="text-xs text-muted-foreground">{option.description}</p>
              </div>
            </Card>
          ))}
        </div>

        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <p className="text-sm font-medium">📖 How it works:</p>
          <ul className="text-sm text-muted-foreground space-y-1 ml-4">
            <li>• Your streak continues as long as you hit your weekly goal</li>
            <li>• Log reading progress, reviews, or focus sessions to count toward your streak</li>
            <li>• Exceed your goal? You get bonus days added to your streak!</li>
            <li>• Miss your goal by one day? A streak freeze will protect you</li>
          </ul>
        </div>

        <div className="flex gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            disabled={saving}
          >
            Skip for now
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
          >
            {saving ? 'Saving...' : 'Set My Goal'}
          </Button>
        </div>
      </div>
    </div>
  );
}
